/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { REPORT_PRICE_BOOK, ReportService } from '@gh/builder-shared';
import { PbCcElementDto, PriceBookRestService, PriceBookUsage, reportIdField } from '@gh/builder-shared-data';
import {
  DateTimeTypes,
  DictionaryService,
  getRouteParam,
  getRouteQueryParam,
  InitViewService,
  isRouteForCreationMode,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import {
  mapSlicedDataItems,
  memoryBasedSlicedDataSource,
  PrivilegedToken,
  SlicedDataSource,
} from '@gh/core-data';
import { TranslationService } from '@gh/core-mls';
import { GridLayoutService } from '@gh/core-ui-grid';
import { currentDate } from '@gh/core-util';
import { map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import {
  PbCcStructure,
  PbDetails,
  PbDetailsGetCostCenterRequest,
  pbDetailsGetCostCenterRequestToDto,
  PbDetailsGetItemsComponentsRequest,
  PbDetailsSave,
  PbDetailsSaveResponse,
  pbDetailsSaveResponseFromDto,
  pbDetailsSaveToDto,
  pbDetailsToDtoForClone,
  PbHousePlan,
  pbHousePlanFromDto,
  PbHousePlanRequest,
  pbHousePlanToCalculateDto,
} from './price-book.model';
import { ENV_BASE_URL } from '@gh/config';

@Injectable()
export class PriceBookDetailsBusinessService implements InitViewService {

  COLUMN_ENABLE_CONDITION = {
    isSalesTax: (): boolean => this.getCountry() === 'us',
  };
  constructor(private priceBookRest: PriceBookRestService,
              private report: ReportService,
              private dictionaryService: DictionaryService,
              private translationService: TranslationService,
              private gridLayouts: GridLayoutService) {

  }

  initView(route: ActivatedRouteSnapshot): any {
    const pbId = getRouteParam(route, 'priceBookId');
    const pbSourceId = getRouteQueryParam(route, 'cloneFrom');

    return {
      priceBookHousePlansGridLayout: this.gridLayouts.get('priceBookHousePlans'),
      priceBookItemsComponentsGridLayout: this.gridLayouts.get('priceBookItemsComponents'),
      priceBookUsages: this.dictionaryService.getByEnum(PriceBookUsage),
      details: isRouteForCreationMode(route) ?
        (pbSourceId ? this.clone(pbSourceId) : this.newDetails())
        : this.get(pbId),
      reportUrl: isRouteForCreationMode(route)
        ? void 0
        : this.report.generateUrl(REPORT_PRICE_BOOK, [reportIdField('PriceBook', pbId)]),
    };
  }

  initBulkUpdateHPMarkups(route: ActivatedRouteSnapshot): any {
    const pbId = getRouteParam(route, 'priceBookId');
    return {
      priceBook: this.get(pbId),
      priceBookHousePlansGridLayout: this.gridLayouts.get('priceBookBulkUpdateHousePlansMarkup'),
    };
  }

  initBulkUpdateICPMarkups(route: ActivatedRouteSnapshot): any {
    const pbId = getRouteParam(route, 'priceBookId');
    return {
      priceBook: this.get(pbId),
      priceBookItemsComponentsGridLayout: this.gridLayouts.get('priceBookBulkUpdateItemsComponentsMarkup', this.COLUMN_ENABLE_CONDITION),
    };
  }

  get(pbId: ID): Observable<PbDetails> {
    return this.priceBookRest.get(pbId);
  }

  clone(pbId: ID): Observable<PbDetails> {
    return this.priceBookRest.clone(pbId).pipe(
      map$((pbDetails) => pbDetailsToDtoForClone(pbDetails, this.generateNewPriceBookName())));
  }

  newDetails(): PbDetails {
    return {
      priceBookId: void 0,
      name: this.generateNewPriceBookName(),
      memo: '',
      defaults: {
        deletePct: 0,
        itemRoundUp: 0.01,
        isQqAllowed: false,
        qqMarkupPct: 0,
        priceRoundUp: 1,
        itemMarkupPct: 0,
        profitMarkupPct: 0,
        componentRoundUp: 0.01,
        overheadMarkupPct: 0,
        componentMarkupPct: 0,
        contingencyMarkupPct: 0,
      },
      housePlanIds: [],
      costCenters: [],
      items: [],
      releaseDate: new Date(2099, 11, 31), // tslint:disable-line:no-magic-numbers
      withdrawalDate: new Date(2099, 11, 31), // tslint:disable-line:no-magic-numbers
    };
  }

  save(model: PbDetailsSave): Observable<PbDetailsSaveResponse> {
    const dto = pbDetailsSaveToDto({ ...model, isPersist: true });
    const save$ = dto.priceBookId ? this.priceBookRest.update(dto) : this.priceBookRest.create(dto);

    return save$.pipe(map$(pbDetailsSaveResponseFromDto));
  }

  recalculate(model: PbDetailsSave): Observable<PbDetailsSaveResponse> {
    return this.priceBookRest
      .calculate(pbDetailsSaveToDto({ ...model, isPersist: false })).pipe(
        map$(pbDetailsSaveResponseFromDto));
  }

  reprice(priceBookId: ID): Observable<PbDetails> {
    return this.priceBookRest.reprice(priceBookId);
  }

  applyDefaults(priceBookId: ID, privilegedToken?: PrivilegedToken): Observable<PbDetails> {
    return this.priceBookRest.applyDefaults(priceBookId, privilegedToken);
  }

  findRelatedHousePlansAsDataSource(request: PbHousePlanRequest): SlicedDataSource<PbHousePlan> {
    return mapSlicedDataItems(this.priceBookRest.findRelatedHousePlansAsDataSource(request), pbHousePlanFromDto);
  }

  findRelatedItemComponentsAsDataSource(filter: string, backupData: any[]): SlicedDataSource<PbCcElementDto> {
    const normalizeFilter = filter.toLowerCase();
    const filteredData = backupData.filter((item) =>
          item.code?.toLowerCase()?.includes(normalizeFilter) || item.name?.toLowerCase()?.includes(normalizeFilter));
    return memoryBasedSlicedDataSource(filteredData);
  }

  loadCostCenter(request: PbDetailsGetCostCenterRequest): Observable<PbCcStructure[]> {
    return this.priceBookRest.findRelatedCcStructures(pbDetailsGetCostCenterRequestToDto(request)).pipe(
      map$((items) => items.map((item) => ({ ...item, costCenterId: request.costCenterId }))));
  }

  loadItemsComponentsByPD(request: PbDetailsGetItemsComponentsRequest): Observable<PbCcElementDto[]> {
    return this.priceBookRest.getAllItemsByPB(request);
  }

  calculateHousePlan(hp: PbHousePlan): Observable<PbHousePlan> {
    return this.priceBookRest.calculateHousePlan(pbHousePlanToCalculateDto(hp)).pipe(
      map$(({ profit, totalPct, calcPrice, costMeter, salesPrice, trueProfit, salesPriceMeter, calcPriceWithGst }) => ({
        ...hp,
        profit, totalPct, calcPrice, costMeter, salesPrice, trueProfit, salesPriceMeter, calcPriceWithGst,
      })));
  }

  getHousePlanListByPBId(params: any): Observable<PbHousePlan[]> {
    return this.priceBookRest.getHousePlanList(params);
  }
  getCountry(): string {
    const baseUrl = ENV_BASE_URL.toString();
    // @ts-ignore
    switch (true) {
      case baseUrl.includes('-au') || baseUrl.includes('.au'):
        return 'au';
      case baseUrl.includes('-nz') || baseUrl.includes('.nz'):
        return 'nz';
      case baseUrl.includes('-us') || baseUrl.includes('.com'):
        return 'us';
      default:
        return 'us';
    }
  }
  private generateNewPriceBookName(): string {
    return `${this.translationService.get('lbl_pb_new')} ${DateTimeTypes.DateTime.format(currentDate())}`;
  }
}
